import {
  type MedicationRefillCandidate,
  type PrescriptionCancelReason,
  type PrescriptionRenewalLink,
  StructuredSigSchema,
} from "@emr/contracts";
import type { PrintablePrescriptionItem } from "../api/clinicalWorkspace";
import { emptyPrescriptionFormValues, type PrescriptionFormValues } from "./PrescriptionFormFields";

/**
 * A signed prescription item as a refill or change starts from it: every field a signed item may
 * carry, any of them missing on an item an older prescription stored in another shape.
 */
export interface SignedItemPrefill {
  readonly medicationDisplay: string;
  readonly sig?: string | undefined;
  readonly quantity?: number | undefined;
  readonly quantityUnit?: string | undefined;
  readonly refills?: number | undefined;
  readonly indication?: string | undefined;
  readonly substitutionAllowed?: boolean | undefined;
  readonly daysSupply?: number | undefined;
  readonly structuredSig?: unknown;
}

/**
 * The form a signed item reopens as: every field as it was signed. The medication is left
 * uncoded and marked drafted, so FDB codes the exact name again, with the attribution the pad
 * must print, the way it codes an AI draft.
 */
export function signedItemFormValues(
  signed: SignedItemPrefill,
  fallbackSig?: string | undefined,
): PrescriptionFormValues {
  const dose = StructuredSigSchema.safeParse(signed.structuredSig);
  return {
    ...emptyPrescriptionFormValues,
    medicationDisplay: signed.medicationDisplay,
    medicationSource: "ai_draft",
    sig: signed.sig ?? fallbackSig ?? "",
    quantity: signed.quantity === undefined ? "" : String(signed.quantity),
    quantityUnit: signed.quantityUnit ?? emptyPrescriptionFormValues.quantityUnit,
    refills: signed.refills === undefined ? "" : String(signed.refills),
    indication: signed.indication ?? "",
    substitutionAllowed: signed.substitutionAllowed ?? true,
    ...(dose.success
      ? {
          doseAmount: String(dose.data.doseAmount),
          doseUnit: dose.data.doseUnit,
          frequencyPerDay: String(dose.data.frequencyPerDay),
          ...(dose.data.durationDays === undefined
            ? {}
            : { durationDays: String(dose.data.durationDays) }),
        }
      : {}),
  };
}

/**
 * Where a refill starts (EXECDEV-101). A medication prescribed at this site starts from the item
 * as it was last signed; any other starts from its chart entry, with quantity and refills left for
 * the physician.
 */
export function refillFormValues(candidate: MedicationRefillCandidate): PrescriptionFormValues {
  const signed = candidate.lastPrescription?.item;
  if (signed === undefined) {
    return {
      ...emptyPrescriptionFormValues,
      medicationDisplay: candidate.display,
      medicationSource: "ai_draft",
      sig: candidate.sig ?? "",
      refills: "",
    };
  }
  return signedItemFormValues(signed, candidate.sig);
}

/** The signed item a refill renews, when one was signed at this site. */
export function refillRenewalLink(
  candidate: MedicationRefillCandidate,
): PrescriptionRenewalLink | undefined {
  const last = candidate.lastPrescription;
  return last === undefined
    ? undefined
    : { printablePrescriptionId: last.printablePrescriptionId, itemIndex: last.itemIndex };
}

/** "no refills", "1 refill", "3 refills". */
export function refillCountLabel(refills: number): string {
  return refills === 0 ? "no refills" : refills === 1 ? "1 refill" : `${refills} refills`;
}

/**
 * "Refilled Metformin 500 mg tablet — 1 tablet by mouth twice daily. Dispense 180 tablet, 3
 * refills." The same wording as the note of a renewal outside a visit
 * (private.prescription_renewal_plan), written into a visit's Plan for a refill signed on it.
 */
export function refillPlanLine(
  item: Pick<
    PrintablePrescriptionItem,
    "medicationDisplay" | "sig" | "quantity" | "quantityUnit" | "refills"
  >,
): string {
  const sig = item.sig.trim();
  return `Refilled ${item.medicationDisplay.trim()}${sig === "" ? "" : ` — ${sig}`}. ${dispenseClause(item)}`;
}

/** "Dispense 180 tablet, 3 refills." — the terms a refill and a change line end with. */
function dispenseClause(
  item: Pick<PrintablePrescriptionItem, "quantity" | "quantityUnit" | "refills">,
): string {
  const unit = item.quantityUnit.trim();
  return `Dispense ${item.quantity}${unit === "" ? "" : ` ${unit}`}, ${refillCountLabel(item.refills)}.`;
}

/** The terms a prescription states, compared to tell a re-issue from a change. */
type PrescriptionTerms = Pick<
  PrintablePrescriptionItem,
  | "medicationDisplay"
  | "sig"
  | "quantity"
  | "quantityUnit"
  | "refills"
  | "indication"
  | "substitutionAllowed"
  | "daysSupply"
>;

/**
 * Whether a medication is listed again exactly as it was signed: the comparison the change note
 * makes (private.prescription_change_plan). Coding and the structured dose are not terms.
 */
export function sameTerms(
  was: { readonly [Term in keyof PrescriptionTerms]?: PrescriptionTerms[Term] | undefined },
  now: PrescriptionTerms,
): boolean {
  return (
    (was.medicationDisplay ?? "").trim() === now.medicationDisplay.trim() &&
    (was.sig ?? "").trim() === now.sig.trim() &&
    was.quantity === now.quantity &&
    (was.quantityUnit ?? "").trim() === now.quantityUnit.trim() &&
    was.refills === now.refills &&
    (was.indication ?? "").trim() === now.indication.trim() &&
    was.substitutionAllowed === now.substitutionAllowed &&
    was.daysSupply === now.daysSupply
  );
}

/**
 * "Changed Metformin 500 mg tablet to Metformin 850 mg tablet — 1 tablet by mouth twice daily.
 * Dispense 180 tablet, 3 refills." The old name appears only when the change renamed the drug;
 * a medication re-issued exactly as it was signed reads "Re-issued …". The same wording as the
 * note of a change outside a visit (private.prescription_change_plan), written into a visit's Plan
 * for a change signed on it, and rebuilt from the change's listing after a reload.
 */
export function changePlanLine(
  was: { readonly medicationDisplay: string; readonly reissued: boolean },
  item: Pick<
    PrintablePrescriptionItem,
    "medicationDisplay" | "sig" | "quantity" | "quantityUnit" | "refills"
  >,
): string {
  const now = item.medicationDisplay.trim();
  const before = was.medicationDisplay.trim();
  const sig = item.sig.trim();
  const name =
    before !== "" && before.toLowerCase() !== now.toLowerCase() ? `${before} to ${now}` : now;
  return `${was.reissued ? "Re-issued" : "Changed"} ${name}${sig === "" ? "" : ` — ${sig}`}. ${dispenseClause(item)}`;
}

const CANCEL_REASON_LABELS: Readonly<Record<PrescriptionCancelReason, string>> = {
  entered_in_error: "entered in error",
  adverse_reaction: "adverse reaction",
  no_longer_needed: "no longer needed",
  patient_declined: "patient declined",
  other: "other",
};

/** "adverse reaction: rash", or what the physician wrote for another reason. */
export function cancelReasonLabel(
  reasonCode: PrescriptionCancelReason,
  reasonText?: string | undefined,
): string {
  const text = reasonText?.trim() ?? "";
  if (reasonCode === "other") return text === "" ? CANCEL_REASON_LABELS.other : text;
  return text === ""
    ? CANCEL_REASON_LABELS[reasonCode]
    : `${CANCEL_REASON_LABELS[reasonCode]}: ${text}`;
}

/**
 * "Cancelled the prescription for Lisinopril 10 mg tablet — adverse reaction: cough. Stopped on
 * the medication list." Written into a visit's Plan for a cancellation made in it, and rebuilt
 * from the visit's listing after a reload. A cancellation outside a visit writes no note.
 */
export function cancelPlanLine(
  medicationDisplay: string,
  reasonCode: PrescriptionCancelReason,
  reasonText: string | undefined,
  stopped: boolean,
): string {
  return `Cancelled the prescription for ${medicationDisplay.trim()} — ${cancelReasonLabel(reasonCode, reasonText)}.${stopped ? " Stopped on the medication list." : ""}`;
}

/**
 * A machine rewrite of the Plan (applying a composed plan, discarding or undoing it) keeps the
 * visit's refill lines: the refills stay signed whatever happens to the plan. Each line the old
 * Plan held and the new one lacks goes back at the end, in one paragraph. A line the physician
 * already deleted is not in the old Plan and stays gone. Lines that would take the Plan past
 * maxLength are left out and returned as unkept, for the caller to warn about.
 */
export function keepRefillPlanLines(
  previousPlan: string,
  nextPlan: string,
  refillLines: readonly string[],
  maxLength: number,
): { readonly plan: string; readonly unkept: readonly string[] } {
  const dropped = [...new Set(refillLines)].filter(
    (line) => previousPlan.includes(line) && !nextPlan.includes(line),
  );
  if (dropped.length === 0) return { plan: nextPlan, unkept: [] };
  const base = nextPlan.trimEnd();
  const kept: string[] = [];
  const unkept: string[] = [];
  for (const line of dropped) {
    const paragraph = [...kept, line].join("\n");
    if ((base === "" ? paragraph : `${base}\n\n${paragraph}`).length <= maxLength) kept.push(line);
    else unkept.push(line);
  }
  if (kept.length === 0) return { plan: nextPlan, unkept };
  const paragraph = kept.join("\n");
  return { plan: base === "" ? paragraph : `${base}\n\n${paragraph}`, unkept };
}

/** One line under a refill's name: what the prefill would sign, or what is still missing. */
export function refillSummary(values: PrescriptionFormValues): string {
  const quantity = values.quantity.trim();
  const refills = values.refills.trim();
  return [
    values.sig.trim() || "Directions not entered",
    quantity === "" ? "Quantity not entered" : `${quantity} ${values.quantityUnit.trim()}`.trim(),
    refills === "" || !/^\d+$/.test(refills)
      ? "Refills not entered"
      : refillCountLabel(Number(refills)),
  ].join(" · ");
}

/** A field the physician must still fill in before the refill can be signed. */
export function refillGap(values: PrescriptionFormValues): string | null {
  if (values.quantity.trim() === "") return "Enter the quantity to dispense.";
  if (values.refills.trim() === "") return "Enter the number of refills (0 for none).";
  if (values.indication.trim() === "") return "Enter the indication.";
  return null;
}
